/**
 * TrialGate — wraps a Pro-tier route and shows an upgrade CTA for users on
 * the 15-day demo trial (or otherwise non-Pro). Admin and Pro/legacy_pro
 * tiers pass through untouched.
 *
 * Demo users see the feature in the nav (so they know it exists) but
 * clicking it lands on this lock screen with a clear upgrade path.
 */
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// Any tier NOT in this set is treated as non-Pro and gated.
// Only "pro" and "legacy_pro" bypass — everyone else (trial, beta, free,
// null, unknown) hits the lock screen.
const PRO_TIERS = new Set(["pro", "legacy_pro"]);

export default function TrialGate({ children, featureName, featureBlurb }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Admin bypasses the gate.
  const tier = user?.tier || "free";
  const isAdmin = user?.role === "admin";
  if (isAdmin || PRO_TIERS.has(tier)) {
    return children;
  }

  return (
    <div className="flex items-center justify-center py-16 px-4" style={{ background: "var(--hoip-bg)" }}
         data-testid={`trial-gate-${featureName?.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="border max-w-lg w-full p-6" style={{
        background: "var(--hoip-card)", borderColor: "var(--hoip-border)"
      }}>
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-4 h-4" style={{ color: "#F59E0B" }} />
          <span className="text-[10px] uppercase tracking-wider font-bold"
                style={{ color: "#F59E0B" }}>Pro feature</span>
        </div>

        <div className="text-xl font-bold mb-2" style={{ color: "var(--hoip-text-primary)" }}>
          {featureName || "This feature"}
        </div>

        <div className="text-[13px] mb-5" style={{ color: "var(--hoip-text-secondary)" }}>
          {featureBlurb || `${featureName} is available on the Pro plan.`}
          {(tier === "trial" || tier === "beta") && (
            <span className="block mt-2 text-[11px] opacity-80">
              During your 15-day demo you have full access to Scanner, Wheel &amp;
              Other Strategies, Portfolio, Trade Log, Backtest Lab, and broker connections.
              This feature is part of the Pro plan (coming soon).
            </span>
          )}
        </div>

        <div className="border p-3 mb-5 text-[11px] space-y-1.5"
             style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.25)" }}>
          <div className="flex items-start gap-1.5">
            <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
            <span style={{ color: "var(--hoip-text-secondary)" }}>
              What you get on Pro: Model Screener across the full sector universe,
              Hedging Lab dispersion + tail-risk sizing, and Reliability Lab
              pre-trade SORM screening.
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            data-testid="trial-gate-upgrade-btn"
            onClick={() => navigate("/billing")}
            className="flex-1 px-4 py-2 text-[12px] font-bold rounded-sm flex items-center justify-center gap-1.5"
            style={{ background: "#F59E0B", color: "#111" }}>
            Upgrade to Pro <ArrowRight className="w-3 h-3" />
          </button>
          <button
            data-testid="trial-gate-back-btn"
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 text-[12px] border rounded-sm"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
